import React from 'react'
import CardLayout from '../../../../components/layouts/CardLayout'
import { ReactComponent as ChartVector } from '../../assets/ChartVector.svg';
import { ReactComponent as ClockVector } from '../../assets/ClockVector.svg';
import { Text, useMainContext } from '../../../../modules/Dashboard';
import TickIcon from '../../../../components/icons/TickIcon';
import InfoCircleIcon from '../../../../components/icons/InfoCircleIcon';
import CalenderIcon from '../../../../components/icons/CalenderIcon';
import ClockIcon from '../../../../components/icons/ClockIcon';
import { formatDateShort, getRemainingDays } from './helper';

const RCurrentPlan = ({ data, loading, isPlanValid }) => {
    const { main: { lang } } = useMainContext()

    if (loading) return <RCurrentPlanSkeleton />
    if (!data || !data.packageData) return <></>

    return (
        <CardLayout
            className={`!p-4 !px-4 sm:!px-6 !rounded-2xl flex flex-col gap-5 
            ${isPlanValid ? "!border-none !bg-[#261B53]" : "!border-[#FF1B1B] !bg-[#270809]"}`}
        >

            {/* Title + Badge */}
            <div className='flex gap-4 items-center justify-between'>
                <Text
                    tid={data.packageData.label[lang]}
                    className={`text-3xl capitalize font-medium
                        ${isPlanValid
                            ? "bg-gradient-to-r from-[#C27AFF] to-[#FC6432] bg-clip-text text-transparent"
                            : "text-[#FFA1A1]"
                        }`}
                />

                <span
                    className={`flex items-center gap-1 px-3 py-1 rounded-lg border
                        ${isPlanValid
                            ? "bg-[#00C95033] border-[#00C9504D]"
                            : "bg-[#270809] border-[#FF1B1B]"
                        }`}
                >
                    {isPlanValid
                        ? <TickIcon size={12} color="#05DF72" />
                        : <InfoCircleIcon size={12} color="#FFA1A1" />
                    }
                    <Text
                        tid={isPlanValid ? "your-active-plan" : "run-out"}
                        className={`text-xs ${isPlanValid ? "text-[#05DF72]" : "text-[#FFA1A1]"}`}
                    />
                </span>
            </div>

            {/* Boxes */}
            <div className='flex flex-col sm:flex-row justify-start gap-3 items-center'>
                <div className='flex items-center gap-2 p-2 rounded-2xl bg-[#00C95033] border border-[#00C9504D] w-full'>
                    <ChartVector height={28} width={28} />
                    <Text tid={"analyses"} className='text-[#05DF72]' />
                    <div className='ml-auto' />
                    <span className='text-lg'>
                        {data.packageData.maxUse - data.packageData.used}
                    </span>
                    <Text tid={"remaining"} className='text-[#05DF72] text-sm' />
                </div>

                <div className='flex items-center gap-2 p-2 rounded-2xl bg-[#59168B33] border border-[#AD46FF33] w-full'>
                    <ClockVector height={28} width={28} />
                    <Text tid={"time-left"} className='text-[#C27AFF]' />
                    <div className='ml-auto' />
                    <span className='text-lg'>
                        {getRemainingDays(data.packageData.expiresAt)}
                    </span>
                    <Text tid={"days"} className='text-[#C27AFF] text-sm text-nowrap' />
                </div>
            </div>

            {/* Dates OR Deactivated Hint */}
            {
                isPlanValid ? (
                    <div className='flex flex-col xs:flex-row gap-4 items-start xs:items-center justify-center mt-1'>
                        <span className='flex items-center gap-1'>
                            <CalenderIcon color='#C27AFF' size={16} />
                            <Text tid={"activated"} className='text-[#C27AFF]' />
                            <span className='text-[#C27AFF] text-sm sm:text-base'>
                                {formatDateShort(data.packageData.createdAt, lang)}
                            </span>
                        </span>

                        <span className='flex items-center gap-1'>
                            <ClockIcon color='#C27AFF' size={16} />
                            <Text tid={"expires"} className='text-[#C27AFF]' />
                            <span className='text-[#C27AFF] text-sm sm:text-base'>
                                {formatDateShort(data.packageData.expiresAt, lang)}
                            </span>
                        </span>
                    </div>
                ) : (
                    <Text tid="plan-deactivated-hint" className="text-[#FFA1A1] mt-1 text-center" />
                )
            }

        </CardLayout>
    )
}

export default RCurrentPlan



const RCurrentPlanSkeleton = () => {
    return (
        <CardLayout className="!p-4 !px-4 sm:!px-6 !rounded-2xl !border-none !bg-[#261B53] flex flex-col gap-5 animate-pulse">

            {/* Title + Active Badge */}
            <div className="flex gap-4 items-center justify-between">
                <div className="h-7 w-40 bg-white/20 rounded-md" />
                <div className="h-6 w-24 bg-white/15 rounded-lg" />
            </div>

            {/* Two Boxes */}
            <div className="flex flex-col xs:flex-row justify-start gap-3 items-center w-full">
                {/* Box 1 */}
                <div className="flex items-center gap-2 p-2 rounded-2xl bg-white/10 border border-white/10 w-full">
                    <div className="w-7 h-7 bg-white/20 rounded-md" />
                    <div className="h-5 w-20 bg-white/20 rounded-md" />
                    <div className="ml-auto" />
                    <div className="h-6 w-10 bg-white/20 rounded-md" />
                </div>

                {/* Box 2 */}
                <div className="flex items-center gap-2 p-2 rounded-2xl bg-white/10 border border-white/10 w-full">
                    <div className="w-7 h-7 bg-white/20 rounded-md" />
                    <div className="h-5 w-24 bg-white/20 rounded-md" />
                    <div className="ml-auto" />
                    <div className="h-6 w-10 bg-white/20 rounded-md" />
                </div>
            </div>

            {/* Date Row */}
            <div className="flex flex-col xs:flex-row gap-4 items-start xs:items-center justify-center mt-1 w-full">
                <div className="flex items-center gap-2 w-fit">
                    <div className="w-4 h-4 bg-white/20 rounded-md" />
                    <div className="h-4 w-24 bg-white/20 rounded-md" />
                </div>

                <div className="flex items-center gap-2 w-fit">
                    <div className="w-4 h-4 bg-white/20 rounded-md" />
                    <div className="h-4 w-24 bg-white/20 rounded-md" />
                </div>
            </div>

        </CardLayout>
    );
};
